import { Box, Grid, Typography, useTheme, alpha } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useDealData } from 'api/aws/deals/useDealData'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch, useSearchData } from 'api/search'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GROBxLoader } from 'theme/components'
import { useApproveDeal } from '../../api/approvals/useApproveDeal'
import { useRejectDeal } from '../../api/approvals/useRejectDeal'
import { useResolveDeal } from '../../api/approvals/useResolveDeal'
import { useSyncQueryCache } from '../../api/aws/organizations_table/useSyncQueryCache'
import { useContact } from '../../api/aws/useContact'
import { useCreateTimeline } from '../../api/aws/useCreateTimeline'
import { useUser } from '../../api/aws/useUser'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { calculateProductsDateRange } from '../../utils/dealCalculations'
import { ROLES } from 'config/roles.config'
import RenderApprover from '../Approvals/RenderApprover'
import { updateDealTimelineData } from '../Timeline/createTimelineData'
import {
  billingInfoMap,
  contractMap,
  csaTotalsMap,
  generalInfoMap,
  ioContractMap,
  ioTotalsMap,
  totalsMap,
} from './dataMaps'
import GenerateInfoTables from './GenerateInfoTables'
import GenerateTable from './GenerateTable'

type MonthsResult = { allSame: boolean; totalMonths: number }

interface ReviewDealProps {
  moduleLocation?: any
  isHeaderExpanded?: any
}

const ReviewDeal = ({ moduleLocation, isHeaderExpanded }: ReviewDealProps) => {
  const { PK, SK } = useParams()
  const dealId = SK?.split('DEAL_')?.[1]
  const queryClient = useQueryClient()
  const { pinned } = useLayoutContext()
  const { deal: currentDeal, productRows: allProductRows, computeTotals } = useDealData(PK, SK, dealId)
  const deal = useMemo(() => {
    if (!PK || !SK || !dealId || !currentDeal) return null
    return currentDeal ?? null
  }, [PK, SK, currentDeal, dealId])
  const productRows = useMemo(() => {
    if (!PK || !SK || !dealId || !currentDeal || !allProductRows || !allProductRows?.length) return []
    return allProductRows?.filter(p => !p?.SK?.startsWith('PRODUCT_CUSTOM'))?.filter(Boolean) ?? []
  }, [PK, SK, currentDeal, dealId, allProductRows])
  const { mutate: approveDeal } = useApproveDeal()
  const { mutate: rejectDeal } = useRejectDeal()
  const { mutate: resolveDeal } = useResolveDeal()
  const { syncDealFields } = useSyncQueryCache()
  const {
    activeUser: { user: activeUser },
  } = useActiveUser()
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const [isApproving, setIsApproving] = useState(false)
  const [rejectDialogs, setRejectDialogs] = useState({})
  const [resolveDialogs, setResolveDialogs] = useState({})
  const [rejectText, setRejectText] = useState('')
  const [resolveText, setResolveText] = useState('')
  const [isRejecting, setIsRejecting] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const { organizations, products } = useEntitiesFromSearch()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const organization = organizations?.find(o => o?.PK === PK) ?? null
  const contractedBy = organizations?.find(o => o?.org_uuid === organization?.contractedBy?.[0]) ?? null
  const { isLoading: isSearchDataLoading, isFetching } = useSearchData()
  // const dealProducts = products?.filter(p => p?.deal_uuid === deal?.deal_uuid) ?? []
  // const productRows = !Array.isArray(dealProducts) ? [dealProducts] : dealProducts
  // const totals = useTotals(productRows, deal?.implementationFee || 0, deal?.type)
  const totals = useMemo(
    () =>
      deal
        ? computeTotals(deal, productRows)
        : {
            grossProfit: 0,
            monthlyTotal: 0,
            mrr: 0,
            serviceMrr: 0,
            setupFee: 0,
            implementationFee: 0,
            spend: 0,
            total: 0,
            totalGrossProfit: 0,
          },
    [deal, productRows, computeTotals]
  )
  const { data: billingContact, isLoading: isBillingContactLoading } = useContact(organization?.billingContact?.[0], [
    'billingContact',
    organization?.billingContact?.[0],
  ])
  const { user: createdBy, isLoading: isCreatedByLoading } = useUser(deal?.createdBy, 'createdBy')
  const approverIds = deal?.approvers?.map(approver => approver?.id)
  const approvers = users?.filter(u => approverIds?.includes(u?.user_uuid))
  const mergedApprovers = deal?.approvers?.map(approver => {
    const matchingUser = approvers?.find(user => user?.user_uuid === approver?.id)
    return {
      ...approver,
      image: matchingUser?.image_512 || null,
    }
  })

  const theme = useTheme()
  const adminIds = [...ROLES.ADMIN_IDS]

  const { allSame, totalMonths } = useMemo<MonthsResult>(() => {
    if (Array.isArray(productRows) && productRows.length > 0) {
      return calculateProductsDateRange(productRows) as MonthsResult
    }
    return { allSame: true, totalMonths: 1 } // consistent shape
  }, [productRows])
  const handleApprove = approverId => {
    if (!dealId || !activeUser) return

    const timelineData = updateDealTimelineData(deal?.deal_uuid, activeUser, 'Approve', 'Approval', 'Deal approved')

    approveDeal(
      {
        PK: deal?.PK,
        SK: deal?.SK,
        approverId,
        requestor: { ...deal?.requestor },
      },
      {
        onSuccess: ({ deal: serverDeal }) => {
          // 1. Update approvers list with the current approval
          const updatedApprovers = serverDeal.approvers.map(a =>
            a.id === approverId
              ? { ...a, hasApproved: true, hasRejected: false, timeApproved: new Date().toISOString() }
              : a
          )

          // 2. Check if ALL have approved
          const allApproved = updatedApprovers.every(a => a.hasApproved)

          // 3. Determine new status
          const newStatus = allApproved ? 'Approved' : serverDeal.status
          const newStage = allApproved ? 'Proposal' : serverDeal.stage

          // 4. Build the complete patch (only include fields that should change)
          const fieldsToUpdate: { fieldName: string; newValue: any }[] = [
            { fieldName: 'approvers', newValue: updatedApprovers },
            { fieldName: 'status', newValue: newStatus },
          ]
          if (allApproved) {
            fieldsToUpdate.push({ fieldName: 'stage', newValue: newStage })
            fieldsToUpdate.push({ fieldName: 'isApproved', newValue: true })
          }

          // 5. Sync SEARCH_DATA_KEY cache
          syncDealFields(queryClient, serverDeal, fieldsToUpdate)

          // 6. Sync item-level cache so agreement tab reads correct data
          const approvalPatch = fieldsToUpdate.reduce(
            (acc, { fieldName, newValue }) => ({ ...acc, [fieldName]: newValue }),
            {} as Record<string, any>
          )
          queryClient.setQueryData(['item', serverDeal.PK, serverDeal.SK], (old: any) => {
            if (!old) return old
            return { ...old, ...approvalPatch }
          })

          // 7. Sync org-level cache in-place (avoids invalidation/refetch race)
          queryClient.setQueryData(['all_org_items', serverDeal.PK], (old: any) => {
            if (!old || !Array.isArray(old)) return old
            return old.map((item: any) => (item.SK === serverDeal.SK ? { ...item, ...approvalPatch } : item))
          })

          // 8. Add a timeline entry for full approval
          if (allApproved) {
            createTimelineEntry(timelineData)
          }
        },
      }
    )
  }

  const handleRejectTextOpen = approverId => {
    setRejectDialogs(prev => ({ ...prev, [approverId]: true }))
  }
  const handleRejectTextClose = approverId => {
    setRejectDialogs(prev => ({ ...prev, [approverId]: false }))
  }
  const handleResolveTextOpen = approverId => {
    setResolveDialogs(prev => ({ ...prev, [approverId]: true }))
  }
  const handleResolveTextClose = approverId => {
    setResolveDialogs(prev => ({ ...prev, [approverId]: false }))
  }
  const handleReject = approverId => {
    if (!dealId || !activeUser) return
    const timelineData = updateDealTimelineData(
      deal?.deal_uuid,
      activeUser,
      'Reject',
      'Approval',
      'Deal rejected',
      rejectText
    )
    try {
      rejectDeal(
        {
          PK: deal?.PK,
          SK: deal?.SK,
          requestor: { ...deal?.requestor },
          approverId,
          reason: rejectText,
        },
        {
          onSuccess: data => {
            const updatedApprovers = deal.approvers.map(approver =>
              approver.id === approverId
                ? {
                    ...approver,
                    hasRejected: true,
                    hasApproved: false,
                    reason: rejectText,
                    timeRejected: new Date().toISOString(),
                  }
                : approver
            )
            const rejectPatch = { status: 'Rejected', approvers: updatedApprovers }
            syncDealFields(queryClient, deal, [
              { fieldName: 'status', newValue: 'Rejected' },
              { fieldName: 'approvers', newValue: updatedApprovers },
            ])
            queryClient.setQueryData(['item', deal.PK, deal.SK], (old: any) => (old ? { ...old, ...rejectPatch } : old))
            queryClient.setQueryData(['all_org_items', deal.PK], (old: any) => {
              if (!old || !Array.isArray(old)) return old
              return old.map((item: any) => (item.SK === deal.SK ? { ...item, ...rejectPatch } : item))
            })
            createTimelineEntry(timelineData)
          },
          onError: e => console.log(e),
        }
      )
      handleRejectTextClose(approverId)
    } catch (error) {
      console.error(error)
    }
  }
  const handleResolve = approverId => {
    if (!dealId || !activeUser) return
    const timelineData = updateDealTimelineData(
      deal?.deal_uuid,
      activeUser,
      'Resolve',
      'Approval',
      'Deal resolved',
      resolveText
    )
    try {
      resolveDeal(
        {
          PK: deal?.PK,
          SK: deal?.SK,
          requestor: { ...deal?.requestor, resolution: resolveText },
          approverId,
          resolution: resolveText,
        },
        {
          onSuccess: data => {
            const updatedRequestor = {
              ...deal?.requestor,
              hasResolved: true,
              resolution: resolveText,
            }
            const updatedApprovers = deal.approvers.map(approver =>
              approver.id === approverId
                ? {
                    ...approver,
                    hasResolved: true,
                    hasRejected: false,
                    resolution: resolveText,
                    timeResolved: new Date().toISOString(),
                  }
                : approver
            )
            const resolvePatch = {
              status: 'Resolved',
              approvers: updatedApprovers,
              requestor: updatedRequestor,
              isResolved: true,
            }
            syncDealFields(queryClient, deal, [
              { fieldName: 'status', newValue: 'Resolved' },
              { fieldName: 'approvers', newValue: updatedApprovers },
              { fieldName: 'requestor', newValue: updatedRequestor },
              { fieldName: 'isResolved', newValue: true },
            ])
            queryClient.setQueryData(['item', deal.PK, deal.SK], (old: any) =>
              old ? { ...old, ...resolvePatch } : old
            )
            queryClient.setQueryData(['all_org_items', deal.PK], (old: any) => {
              if (!old || !Array.isArray(old)) return old
              return old.map((item: any) => (item.SK === deal.SK ? { ...item, ...resolvePatch } : item))
            })
            createTimelineEntry(timelineData)
          },
          onError: e => console.log(e),
        }
      )
      handleResolveTextClose(approverId)
    } catch (error) {
      console.error(error)
    }
  }
  // Use your moduleHeight (or similar) to control overall container height.
  const ncoHeight =
    pinned && !isHeaderExpanded
      ? `calc(90vh - 35px)`
      : !pinned && isHeaderExpanded
      ? `calc(90vh - 125px)`
      : pinned && isHeaderExpanded
      ? `calc(90vh - 175px)`
      : 'calc(100vh - 70px)'
  const calcHeight =
    pinned && !isHeaderExpanded
      ? `calc(80vh - 5px)`
      : !pinned && isHeaderExpanded
      ? `calc(80vh - 135px)`
      : pinned && isHeaderExpanded
      ? `calc(80vh - 135px)`
      : '100vh'
  const moduleHeight = moduleLocation === 'NcoGridDrawer' ? ncoHeight : calcHeight
  const detailsHeight = 'calc(100vh - 25%)'
  const isLoading = isBillingContactLoading || isCreatedByLoading || isUsersLoading || isSearchDataLoading || isFetching
  if (isLoading) {
    // return moduleLocation === 'NcoGridDrawer' ? <DrawerLoading /> : <DetailsLoading />
    return <GROBxLoader />
  }
  const uiHeight = moduleLocation === 'NcoGridDrawer' ? moduleHeight : detailsHeight
  const density = moduleLocation === 'NcoGridDrawer' ? 'compact' : 'standard'

  return (
    <Grid
      container
      sx={{
        height: uiHeight,
        overflow: 'auto',
        transition: 'height 0.3s ease',
        p: 2,
      }}
    >
      {/* Info Sections */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {/* Approvers */}
          <Grid item xs={12} sm={6} md={3}>
            {mergedApprovers && mergedApprovers.length > 0 ? (
              mergedApprovers.map(approver => (
                <RenderApprover
                  key={approver.id}
                  activeUser={activeUser}
                  approver={approver}
                  requestor={deal?.requestor}
                  isApproving={isApproving}
                  onApprove={() => handleApprove(approver.id)}
                  rejectText={rejectText}
                  setRejectText={setRejectText}
                  isRejecting={isRejecting}
                  handleRejectTextOpen={() => handleRejectTextOpen(approver.id)}
                  handleRejectTextClose={() => handleRejectTextClose(approver.id)}
                  isRejectTextOpen={!!rejectDialogs[approver.id]}
                  handleReject={() => handleReject(approver.id)}
                  resolveText={resolveText}
                  setResolveText={setResolveText}
                  isResolving={isResolving}
                  isResolveTextOpen={!!resolveDialogs[approver.id]}
                  handleResolveTextOpen={() => handleResolveTextOpen(approver.id)}
                  handleResolveTextClose={() => handleResolveTextClose(approver.id)}
                  handleResolve={() => handleResolve(approver.id)}
                  requestedTime={deal.requestedTime || ''}
                  isResolved={deal?.isResolved || false}
                  timeResolved={deal?.timeResolved}
                  resolution={deal?.resolution || null}
                  density={density}
                />
              ))
            ) : (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                  minHeight: '330px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: alpha(theme.palette.text.primary, 0.02),
                }}
              >
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>No approvers assigned</Typography>
              </Box>
            )}
          </Grid>
          {/* General Info */}
          <Grid item xs={12} sm={6} md={3}>
            <GenerateInfoTables options={generalInfoMap(organization)} />
          </Grid>
          {/* Totals Info */}
          <Grid item xs={12} sm={6} md={3}>
            <GenerateInfoTables
              options={
                deal && deal?.type === 'IO'
                  ? ioTotalsMap(deal, totals, allSame)
                  : ['CSA', 'RCSA'].includes(deal?.type)
                  ? csaTotalsMap(deal, totals)
                  : totalsMap(deal, totals, allSame)
              }
            />
          </Grid>
          {/* Billing Info */}
          <Grid item xs={12} sm={6} md={3}>
            <GenerateInfoTables options={billingInfoMap(deal, contractedBy, createdBy, billingContact)} />
          </Grid>
        </Grid>
      </Grid>

      {/* Products Table */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
          <GenerateTable
            options={
              deal && deal.type === 'IO'
                ? ioContractMap(productRows ?? [], theme)
                : contractMap(productRows ?? [], theme)
            }
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default ReviewDeal
